import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import ExploreIcon from '@mui/icons-material/Explore';
import SecurityIcon from '@mui/icons-material/Security';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GroupsIcon from '@mui/icons-material/Groups';
import PublicIcon from '@mui/icons-material/Public';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';

const heroSx = {
  position: 'relative' as const,
  minHeight: { xs: 280, sm: 340, md: 460 },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: 'url("/images/cappadocia-travel.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: { xs: 'scroll', md: 'fixed' },
  color: '#FFFFFF',
  textAlign: 'center' as const,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    bgcolor: 'rgba(15, 23, 42, 0.58)',
  },
};

export function AboutPage() {
  const values = [
    {
      icon: <ExploreIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: 'Authentic Experiences',
      description: 'We connect travelers with local guides who share genuine stories and hidden gems.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: 'Trust & Safety',
      description: 'Every guide is verified and reviewed to ensure safe, reliable experiences.',
    },
    {
      icon: <FavoriteIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: 'Passion for Travel',
      description: 'We believe in meaningful connections and transformative travel experiences.',
    },
    {
      icon: <GroupsIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: 'Community First',
      description: 'Supporting local guides and empowering communities around the world.',
    },
  ];

  const stats = [
    { value: '10,000+', label: 'Happy Travelers' },
    { value: '500+', label: 'Expert Guides' },
    { value: '100+', label: 'Destinations' },
    { value: '50+', label: 'Countries' },
  ];

  return (
    <Box sx={{ width: '100%', overflowX: 'clip' }}>
      {/* Hero Section */}
      <Box sx={heroSx}>
        <Container
          maxWidth="md"
          sx={{ position: 'relative', zIndex: 1, py: { xs: 5, md: 6 }, px: { xs: 2.5, sm: 3 } }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: { xs: 1.5, md: 3 },
              color: '#FFFFFF',
              textShadow: '0 2px 12px rgba(0,0,0,0.35)',
              lineHeight: 1.2,
            }}
          >
            About Triplin
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.9375rem', sm: '1.125rem', md: '1.5rem' },
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: 700,
              mx: 'auto',
              color: 'rgba(255,255,255,0.92)',
              textShadow: '0 1px 6px rgba(0,0,0,0.3)',
              px: { xs: 0.5, md: 0 },
            }}
          >
            Connecting travelers with authentic local guides for unforgettable experiences around the
            world
          </Typography>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src="/images/destinations/about-community.jpg"
              alt="Travelers exploring with a local guide"
              sx={{
                width: '100%',
                height: { xs: 220, sm: 300, md: 400 },
                objectFit: 'cover',
                borderRadius: { xs: 2, md: 3 },
                boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
                display: 'block',
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1.5,
                display: 'block',
              }}
            >
              Our Story
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 700,
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '1.5rem', md: '2.25rem' },
                lineHeight: 1.25,
              }}
            >
              Redefining Travel Experiences
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                mb: 2,
                lineHeight: 1.8,
                fontSize: { xs: '0.9375rem', md: '1rem' },
              }}
            >
              Triplin was born from a simple idea: that the best way to experience a destination is
              through the eyes of a local. We believe that authentic travel experiences come from
              genuine connections with people who know their cities inside and out.
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                mb: 2,
                lineHeight: 1.8,
                fontSize: { xs: '0.9375rem', md: '1rem' },
              }}
            >
              Our platform empowers local guides to share their passion, knowledge, and culture with
              travelers from around the world. At the same time, we help travelers discover hidden
              gems, local traditions, and authentic experiences that guidebooks can&apos;t capture.
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                lineHeight: 1.8,
                fontSize: { xs: '0.9375rem', md: '1rem' },
              }}
            >
              Today, we&apos;re proud to connect thousands of travelers with expert local guides
              across hundreds of destinations worldwide, creating memories that last a lifetime.
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Values Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 5, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1.5,
                display: 'block',
              }}
            >
              Our Values
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: '1.5rem', md: '2.25rem' },
              }}
            >
              What We Stand For
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '0.9375rem', md: '1rem' },
                px: { xs: 1, md: 0 },
              }}
            >
              Our core values guide everything we do, from selecting guides to designing experiences
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, md: 4 }}>
            {values.map((value) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={value.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    textAlign: 'center',
                    bgcolor: 'white',
                    borderRadius: { xs: 2, md: 3 },
                    transition: 'all 0.3s ease',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      transform: { md: 'translateY(-8px)' },
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 64, md: 80 },
                      height: { xs: 64, md: 80 },
                      borderRadius: '50%',
                      bgcolor: alpha('#1976d2', 0.1),
                      color: 'primary.main',
                      mb: { xs: 2, md: 3 },
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography
                    sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: '1.0625rem', md: '1.25rem' } }}
                  >
                    {value.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', md: '0.9375rem' },
                    }}
                  >
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: 1.5,
              mb: 1.5,
              display: 'block',
            }}
          >
            Our Impact
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '2.25rem' },
            }}
          >
            Travel by the Numbers
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2.5, md: 4 }}>
          {stats.map((stat) => (
            <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
              <Box sx={{ textAlign: 'center', px: { xs: 0.5, md: 0 } }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 0.5,
                    fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: { xs: '0.8125rem', md: '1.125rem' },
                    lineHeight: 1.35,
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Mission Section */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 5, md: 12 },
          background: `linear-gradient(135deg, ${alpha('#1976d2', 0.94)} 0%, ${alpha('#0d47a1', 0.94)} 100%), url('/images/rome.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          color: '#FFFFFF',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2.5, sm: 3 } }}>
          <Box sx={{ textAlign: 'center' }}>
            <PublicIcon sx={{ fontSize: { xs: 44, md: 60 }, mb: { xs: 2, md: 3 }, opacity: 0.9 }} />
            <Typography
              component="h2"
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 700,
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '1.5rem', md: '2.25rem' },
                color: '#FFFFFF',
              }}
            >
              Our Mission
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                lineHeight: 1.8,
                mb: { xs: 3, md: 4 },
                opacity: 0.95,
                fontSize: { xs: '0.9375rem', md: '1.125rem' },
                color: '#FFFFFF',
              }}
            >
              To transform the way people travel by creating meaningful connections between travelers
              and local communities, fostering cultural exchange, and empowering guides to share
              their passion and expertise.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 3 }}
              justifyContent="center"
              alignItems="center"
            >
              {[
                { icon: <VerifiedUserIcon sx={{ fontSize: 28 }} />, label: 'Verified Guides' },
                { icon: <FavoriteIcon sx={{ fontSize: 28 }} />, label: 'Authentic Experiences' },
                { icon: <GroupsIcon sx={{ fontSize: 28 }} />, label: 'Global Community' },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                  }}
                >
                  {item.icon}
                  <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.9375rem', md: '1rem' } }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1.5,
                display: 'block',
              }}
            >
              Why Choose Us
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 700,
                mb: { xs: 2.5, md: 3 },
                fontSize: { xs: '1.5rem', md: '2.25rem' },
                lineHeight: 1.25,
              }}
            >
              Experience the Difference
            </Typography>
            <Stack spacing={{ xs: 2.5, md: 3 }}>
              {[
                {
                  title: 'Local Expertise',
                  body: "Our guides are locals who live and breathe their cities. They don't just show you the sights—they share stories, traditions, and insider knowledge.",
                },
                {
                  title: 'Personalized Tours',
                  body: 'Every traveler is unique. Our guides customize experiences based on your interests, pace, and preferences.',
                },
                {
                  title: 'Safe & Secure',
                  body: 'All guides are thoroughly vetted, verified, and reviewed. Your safety and satisfaction are our top priorities.',
                },
              ].map((item) => (
                <Box key={item.title}>
                  <Typography
                    sx={{ fontWeight: 600, mb: 0.75, fontSize: { xs: '1rem', md: '1.125rem' } }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                      fontSize: { xs: '0.9375rem', md: '1rem' },
                    }}
                  >
                    {item.body}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Button
              component={RouterLink}
              to="/guides"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                mt: { xs: 3, md: 4 },
                px: 3,
                py: 1.25,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Find a Guide
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }}>
            <Box
              component="img"
              src="/images/photography-tours.webp"
              alt="Guide capturing local culture with travelers"
              sx={{
                width: '100%',
                height: { xs: 220, sm: 300, md: 400 },
                objectFit: 'cover',
                borderRadius: { xs: 2, md: 3 },
                boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
                display: 'block',
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
